import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import CloseIcon from '@mui/icons-material/Close';

import { formatDistance } from 'date-fns';

import { useNavigationStack } from '../../../../context/NavigationStackContext';
import type { IPostsViewComment } from '../../../../redux/api/posts/posts-api.types';
import { AvatarSize, AvatarTypes } from '../../../../shared/constants';
import { Paths } from '../../../../shared/paths';
import { getDateFnsLocale } from '../../../../utils/datetime/date-time-utils';
import {
  formatLikeCount,
  getDisplayReactionCount,
} from '../../../../utils/ui/format-like-count';
import AddReactionActionButton from '../../../designe/ActionButtons/AddReactionActionButton';
import ApplyActionButton from '../../../designe/ActionButtons/ApplyActionButton';
import AttachFilesActionButton from '../../../designe/ActionButtons/AttachFilesActionButton';
import CancelActionButton from '../../../designe/ActionButtons/CancleActionButton';
import DeleteActionButton from '../../../designe/ActionButtons/DeleteActionButton';
import EditActionButton from '../../../designe/ActionButtons/EditActionButton';
import RemoveReactionActionButton from '../../../designe/ActionButtons/RemoveReactionActionButton';
import ReplyActionButton from '../../../designe/ActionButtons/ReplyActionButton';
import AppUserAvatar from '../../../designe/app-entity-avatar/app-user-avatar/AppUserAvatar';
import AppFile from '../../../designe/app-file/AppFile';
import AppTextField from '../../../designe/textfields/app-text-field/AppTextField';
import ConfirmDialog from '../../confirm-dialog/ConfirmDialog';
import { useStyles } from './Comment.styles';
import { useComment } from './useComment';

interface CommentProps {
  comment: IPostsViewComment;
  repliedComment?: boolean;
  onCommentReply?: (comment: IPostsViewComment) => void;
  onCommentDeleteFromReply?: () => void;
  allowReply?: boolean;
  refreshPosts?: () => void;
}

export default function Comment(props: CommentProps) {
  const {
    comment,
    repliedComment,
    onCommentReply,
    onCommentDeleteFromReply,
    allowReply = true,
    refreshPosts,
  } = props;
  const { navigate } = useNavigationStack();
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const {
    onCommentRemove,
    onCommentSave,
    onCommentReaction,
    onCommentEdit,
    onToggleToEditComment,
    editedComment,
    commentFiles,
    onUploadFiles,
    onDeleteFile,
    userReaction,
    onCommentCancel,
    user,
    isSavingComment,
    isDeletingComment,
  } = useComment(comment, refreshPosts);

  function renderCommentReactionIcon() {
    return userReaction ? (
      <RemoveReactionActionButton onClick={onCommentReaction} />
    ) : (
      <AddReactionActionButton onClick={onCommentReaction} />
    );
  }

  const isCommentLiked =
    userReaction !== null
      ? userReaction
      : !!comment.reactions?.find((r) => r.userId === user?.id);

  function onUserClick(userId) {
    navigate(Paths.users.toUser(userId));
  }

  return (
    <div className={classes.commentWrapper}>
      <div className={classes.header}>
        <Box display="flex" justifyContent="space-between" width="100%">
          <Box
            display="flex"
            gap={1}
            onClick={() => onUserClick(comment.owner.id)}>
            <AppUserAvatar
              entity={comment.owner}
              size={AvatarSize.m}
              type={AvatarTypes.round}
            />
            <Box className={classes.userInfo}>
              <Typography variant="text-lg">
                {`${comment.owner.firstname} ${comment.owner.lastname || ''}`}
              </Typography>
              <Typography variant="text-sm" className={classes.createdAt}>
                {formatDistance(new Date(comment.createdAt), new Date(), {
                  addSuffix: true,
                  locale: getDateFnsLocale(i18n.language),
                })}
              </Typography>
            </Box>
          </Box>
          {/* add permissions */}
          <Box>
            {!repliedComment &&
              !editedComment &&
              (comment.ownerId === user?.id ||
                comment.pageUserId === user?.id) && (
                <Box display="flex" gap={1}>
                  {comment?.permissions?.canEdit && (
                    <EditActionButton onClick={onToggleToEditComment} />
                  )}
                  {comment?.permissions?.canDelete && (
                    <DeleteActionButton
                      onClick={() => setDeleteConfirm(true)}
                    />
                  )}
                </Box>
              )}
            {repliedComment && onCommentDeleteFromReply && (
              <CloseIcon onClick={() => onCommentDeleteFromReply()} />
            )}
          </Box>
        </Box>
      </div>

      <Box marginLeft={7}>
        <Box>
          {comment.parent && !repliedComment && (
            <div className={classes.repliedCommentWrapper}>
              <Comment comment={comment.parent} repliedComment />
            </div>
          )}

          {!editedComment && (
            <Typography variant="text-md" className={classes.commentContent}>
              {comment.content}
            </Typography>
          )}
          {editedComment && (
            <div className={classes.editedCommentContent}>
              {!!commentFiles?.length && (
                <div className={classes.filesWrapper}>
                  {commentFiles.map((file) => (
                    <AppFile
                      key={file.id}
                      file={file}
                      onDelete={(file) => onDeleteFile(file.id)}
                    />
                  ))}
                </div>
              )}
              <Box display="flex" gap={0.5}>
                <AppTextField
                  fullWidth
                  multiline
                  maxRows={5}
                  value={editedComment.content}
                  onChange={(e) => onCommentEdit(e.target.value)}
                />

                <AttachFilesActionButton
                  onFileInput={(e) => onUploadFiles(e.target.files)}
                />
                <ApplyActionButton
                  disabled={
                    !editedComment.content && !editedComment?.files?.length
                  }
                  loading={isSavingComment}
                  onClick={onCommentSave}
                />
                <CancelActionButton onClick={onCommentCancel} />
              </Box>
            </div>
          )}

          {!editedComment && !!commentFiles.length && (
            <div className={classes.filesWrapper}>
              {commentFiles.map((file) => (
                <AppFile key={file.id} file={file} />
              ))}
            </div>
          )}
        </Box>
        {!repliedComment && onCommentReply && !editedComment && (
          <Box
            marginTop={theme.spacing(1)}
            display="flex"
            gap={1}
            alignItems="center">
            <Box display="flex" alignItems="center" gap={0.5}>
              {renderCommentReactionIcon()}
              {comment.reactions.length > 0 && (
                <Typography
                  component="span"
                  variant="text-sm"
                  sx={{
                    color: isCommentLiked
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                  }}>
                  {formatLikeCount(
                    getDisplayReactionCount(
                      comment.reactions,
                      user?.id,
                      userReaction,
                    ),
                  )}
                </Typography>
              )}
            </Box>
            {allowReply && (
              <ReplyActionButton onClick={() => onCommentReply(comment)} />
            )}
          </Box>
        )}
      </Box>

      {deleteConfirm && (
        <ConfirmDialog
          open={!!deleteConfirm}
          onClose={() => setDeleteConfirm(null)}
          title={t('common.delete')}
          isLoading={isDeletingComment}
          onSubmit={() => {
            void onCommentRemove().finally(() => setDeleteConfirm(null));
          }}>
          {t('common.deleteQuestion')}
        </ConfirmDialog>
      )}
    </div>
  );
}
